/** @jsxImportSource @emotion/react */
import React from 'react';
import { jsx, css } from '@emotion/react';
import { arrayMutators, createDecorator, Form, validate } from '../../components/form';
import SaleEditSplitPane from './components/SaleEditSplitPane';
import Spinner from '@atlaskit/spinner';
import { PageWrapper } from '../../components/page';
import SaleEditHeader from './SaleEditHeader';
import SaleForm from './SaleForm';
import SaleEditFooter from './components/SaleEditFooter';
import { useCurrentCompany } from '../auth/CurrentCompany';
import InvoiceSettingsSplitPane from './components/InvoiceSettingsSplitPane';
import InvoiceSettingsForm from './InvoiceSettingsForm';
import PageHeader from '@atlaskit/page-header';
import { FormattedMessage } from 'components/i18n';
import Button from '../../components/button';
import { queryCache, useMutation } from 'react-query';
import { useNotifications } from '../../components/notification';
import { Sale, SaleEmail } from './types';
import api from '../../libs/api';
import { useAuth } from '../auth';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import { ButtonGroup } from '@atlaskit/button';

type Props = {}

const updateInvoiceSettings = async (values: any) => {
  const {companyId, logo, ...invoiceSettings} = values;
  const payload = new FormData();
  // Object.keys(invoiceSettings).forEach(key => payload.append(key, invoiceSettings[key]));
  payload.append('data', JSON.stringify(invoiceSettings))
  if(invoiceSettings.logoUrl && logo) {
    payload.append("logo", logo);
  }
  return api.put(`/Companies/${companyId}/invoice-settings`, payload);
};

const schema = yup.object({
  companyInformation: yup.object({
    address: yup.object({
      line1: yup.string().required('Required'),
      line2: yup.string(),
      postalCode: yup.string().required('Required'),
      city: yup.string().required('Required'),
      country: yup.string().required('Required'),
    }).required('Required'),
    companyRegistrationNumber: yup.string(),
    vatIdentificationNumber: yup.string(),
    bankAccountNumber: yup.string(),
    email: yup.string(),
    phone: yup.string(),
  }).required(),
});

const InvoiceSettingsPage: React.FC<Props> = (props) => {
  const currentCompany = useCurrentCompany();
  const {currentUser} = useAuth();
  const isLoading = false;
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const [update, { status, isLoading: isSaving }] = useMutation(updateInvoiceSettings, {
    onSuccess: () => {
      queryCache.invalidateQueries(['company', currentCompany?.slug]);
      showSuccessNotification(
        <FormattedMessage
          id="sale.InvoiceSettingsPage.notifications.save.success.title`"
          defaultMessage="Invoice settings saved"
        />);
      // navigate(`../`);
    },
    onError: (e: any) => {
      showErrorNotification(
        e.message
      )}
  });

  if(!currentCompany) {
    return <span/>
  }

  let initialValues = currentCompany.invoiceSettings;

  if(!initialValues) {
    initialValues = {
      language: 'nl',
      invoiceTemplateId: '1',
      logoUrl: '',
      fontFamily: 'Helvetica',
      fontSize: 9,
      textLogo: currentCompany.name,
      accentColor: '#000000',
      textLogoColor: '#13C181',
      logoHeight: 40,
      logoPosition: 'left',
      logoMarginTop: 2.7,
      invoiceInformationMarginTop: 3.9,
      companyInformationPosition: 'top',
      invoiceInformationLayout: 'horizontal',
      descriptionMarginTop: 5.8,
      noteMarginTop: 7,
      companyInformation: {
        name: currentCompany.name,
        companyRegistrationNumber: currentCompany.companyRegistrationNumber,
        vatIdentificationNumber: (currentCompany.businessType || '').match('B.V.|V.O.F.')? currentCompany.vatIdentificationNumber : '',
        bankAccountNumber: currentCompany.bankAccountNumber,
        address: currentCompany.address,
      },
      defaultDescription: 'Betreft\nWerkzaamheden {{factuur.maand}}',
      defaultNote: `Notitie\nGraag het totaalbedrag van € {{bedrag factuur.totaalbedrag}} voor {{datum factuur.vervaldatum}} overmaken op {{bedrijf.bankrekeningnummer}} onder vermelding van factuurnummer {{factuur.factuurnummer}}.`,
      defaultFooter: 'Voor al onze diensten gelden de algemene voorwaarden.',
      emailTemplate: {
        bcc: currentUser?.email,
        subject: `Factuur {{factuur.factuurnummer}} van ${currentCompany.name}`,
        body: 'Beste {{klant.voornaam}},\n' +
          '\n' +
          'Bijgaand ontvang je onze factuur {{factuur.factuurnummer}}.\n' +
          '\n' +
          'Graag het totaalbedrag van € {{bedrag factuur.totaalbedrag}} voor {{datum factuur.vervaldatum}} overmaken op {{bedrijf.bankrekeningnummer}} onder vermelding van factuurnummer {{factuur.factuurnummer}}.\n' +
          '\n' +
          'Met vriendelijke groet,' +
          '\n'+
          `${currentUser?.name}`
      },

      recurringEmailTemplate: {
        bcc: currentUser?.email,
        subject: `Factuur {{factuur.factuurnummer}} van ${currentCompany.name}`,
        body: 'Beste {{klant.voornaam}},\n' +
          '\n' +
          'Bijgaand ontvang je onze factuur {{factuur.factuurnummer}} voor de maand {{factuur.maand}}.\n' +
          '\n' +
          'Graag het totaalbedrag van € {{bedrag factuur.totaalbedrag}} voor {{datum factuur.vervaldatum}} overmaken op {{bedrijf.bankrekeningnummer}} onder vermelding van factuurnummer {{factuur.factuurnummer}}.\n' +
          '\n' +
          'Met vriendelijke groet,' +
          '\n'+
          `${currentUser?.name}`
      }
    };
  }

  return <Form<any>
    initialValues={initialValues}
    onSubmit={async (values) => {
      await update({...values, companyId: currentCompany.id});
    }}
    validate={validate(schema)}
    subscription={{ submitting: true, pristine: true }}
  >
    {(formProps) => {
      return (
        <InvoiceSettingsSplitPane>
          {!currentCompany || isLoading?  <Spinner/>  :
            <PageWrapper fullWidth>
              <PageHeader actions={
                <ButtonGroup>
                  <Button appearance="subtle" onClick={() => {
                    formProps.form.reset();
                  }}> <FormattedMessage
                    id="common.cancel"
                    defaultMessage="Cancel"
                  /></Button>
                  <Button appearance="default" type="submit" onClick={() => {
                formProps.form.submit();
              }}> <FormattedMessage
                id="sale.InvoiceSettingsForm.save"
                defaultMessage="Save"
                /></Button>

                </ButtonGroup>}><FormattedMessage
                id='sale.InvoiceSettingsPage.title'
                defaultMessage='Invoice settings'
              /></PageHeader>

              <InvoiceSettingsForm {...formProps} isSaving={false} />
              {/*<SaleEditFooter isNew={isNew}  isSaving={isSaving} {...formProps}/>*/}
            </PageWrapper>}
        </InvoiceSettingsSplitPane>
      );
    }}
  </Form>;
};

export default InvoiceSettingsPage;
